import { useState, useRef } from "react";
import styled from "styled-components";
import { SiBookstack } from "react-icons/si";
import { TiTick } from "react-icons/ti";
import { useComparisonState } from "../hooks/useComparisonState";

interface ComparisonDimensionFilterProps {
  dimensions: Record<string, { dimension_key: string; dimension_label: string }[]>;
}

const ComparisonDimensionFilter = ({ dimensions }: ComparisonDimensionFilterProps) => {
  const { cmp_mode, cmp_dim, setDimension } = useComparisonState();
  const [isOpen, setIsOpen] = useState(false);

  if (!cmp_mode || !dimensions?.[cmp_mode]) return null;

  const dimensionList = dimensions[cmp_mode];

  const handleSelect = (key: string) => {
    setDimension(cmp_dim === key ? null : key);
    setIsOpen(false);
  };

  return (
    <Wrapper>
      <button className="dim-trigger" onClick={() => setIsOpen((o) => !o)}>
        <SiBookstack size={20} />
        {cmp_dim && <span className="active-dot" />}
      </button>

      {isOpen && (
        <>
          <div className="backdrop" onClick={() => setIsOpen(false)} />
          <div className="dim-panel">
            <div className="panel-header">Compare Categories</div>
            {dimensionList.map((d) => (
              <div
                key={d.dimension_key}
                className={`dim-item ${cmp_dim === d.dimension_key ? "active" : ""}`}
                onClick={() => handleSelect(d.dimension_key)}
              >
                <span>{d.dimension_label}</span>
                {cmp_dim === d.dimension_key && <TiTick />}
              </div>
            ))}
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default ComparisonDimensionFilter;

const Wrapper = styled.div`
  position: relative;

  .dim-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 6px 14px;
    height: 100%;
    min-height: 40px;
    border: none;
    border-radius: 8px;
    background: ${({ theme }) => theme.widget.background};
    color: ${({ theme }) => theme.text};
    cursor: pointer;

    .active-dot {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #c62828;
      border: 2px solid ${({ theme }) => theme.widget.background};
    }
  }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
  }

  .dim-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1000;
    min-width: 220px;
    padding: 12px;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border_color || "#e0e0e0"};
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  .panel-header {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    padding: 4px 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.border_color || "#eee"};
    margin-bottom: 6px;
  }

  .dim-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 8px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    transition: background 0.15s;

    &:hover {
      background: ${({ theme }) => theme.body};
    }

    &.active {
      background: rgba(13, 138, 106, 0.08);
      font-weight: 500;
    }
  }
`;
